import React, { useState, useEffect, useImperativeHandle, useRef } from 'react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../config/localization';
import BaseWidget from './BaseWidget';
import { Icon, ICONS } from '../ui/icons';
import CustomSelect from '../ui/inputs/CustomSelect';
import MultiSelect from '../ui/inputs/MultiSelect';
import Tooltip from '../ui/Tooltip';
import ImageSizeInput from '../ui/inputs/ImageSizeInput';
import SeedInput from '../ui/inputs/SeedInput';
import { parseDropdownOptions } from '../../utils/dropdownOptions';
import { getControllerField, getEffectiveConfig } from '../../utils/controllerVariants';
import { clampSeed, nextRandomSeed, normalizeSeedBounds, offsetSeed } from '../../utils/seedRolling';
import { persistSeedAdvance, subscribeToSeedStorageSync } from '../../utils/seedStorageSync';
import type { IWorkflowWidget, WidgetPayloadResult, WidgetValidationError } from '../../interfaces/IWorkflowWidget';

// Field definition from embedded dynamic widget config
interface EmbeddedField {
  field_id: string;
  field_type: string;
  label: string;
  tooltip?: string | null;
  display_order: number;
  config_data: Record<string, any>;
}

// Dynamic widget configuration stored in workflow
interface DynamicWidgetConfig {
  name: string;
  description?: string | null;
  display_order: number;
  fields: EmbeddedField[];
}

interface EmbeddedDynamicWidgetRendererProps {
  workflowId: number;
  config: DynamicWidgetConfig;
  selectedLanguage: Language;
  widgetRef?: React.RefObject<IWorkflowWidget>;
  className?: string;
}

function parseMultiselectValue(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  return trimmed.split(',');
}

function parseMaxItems(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : undefined;
}

export function normalizeEmbeddedMultiselectValue(
  value: unknown,
  optionsValue: string | unknown[] | null | undefined,
  maxItems?: unknown
): string[] {
  const options = parseDropdownOptions(optionsValue);
  const validValues = new Set(options.map(option => option.value));
  const normalized = Array.from(new Set(
    parseMultiselectValue(value)
      .map(item => String(item).trim())
      .filter(item => item !== '' && validValues.has(item))
  ));

  const parsedMaxItems = parseMaxItems(maxItems);
  return parsedMaxItems !== undefined
    ? normalized.slice(0, parsedMaxItems)
    : normalized;
}

export default function EmbeddedDynamicWidgetRenderer({
  workflowId,
  config,
  selectedLanguage,
  widgetRef,
  className = ''
}: EmbeddedDynamicWidgetRendererProps) {
  const { currentOrganization } = useOrganization();
  const t = useLocalization(selectedLanguage);
  const [values, setValues] = useState<Record<string, any>>({});
  const [fixedSeeds, setFixedSeeds] = useState<Record<string, boolean>>({});
  const valuesRef = useRef(values);
  const fixedSeedsRef = useRef(fixedSeeds);
  const mountedRef = useRef(true);
  valuesRef.current = values;
  fixedSeedsRef.current = fixedSeeds;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const buildStorageKey = (fieldId: string) =>
    `embedded_widget_${workflowId}_${fieldId}_${currentOrganization?.id}`;

  const buildFixedStorageKey = (fieldId: string) =>
    `${buildStorageKey(fieldId)}_fixed`;

  useEffect(() => subscribeToSeedStorageSync(({ storageKey, value }) => {
    const seedField = config.fields.find(candidate => (
      candidate.field_type === 'seed' && buildStorageKey(candidate.field_id) === storageKey
    ));
    if (seedField) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return;
      const activeControllerField = getControllerField(config.fields);
      const activeControllerValue = activeControllerField
        ? valuesRef.current[activeControllerField.field_id]
        : undefined;
      const effectiveConfig = activeControllerField
        ? getEffectiveConfig(
            seedField,
            activeControllerValue !== undefined && activeControllerValue !== null
              ? String(activeControllerValue)
              : undefined
          ).config
        : seedField.config_data;
      const nextValue = clampSeed(
        numericValue,
        effectiveConfig?.min_value,
        effectiveConfig?.max_value
      );
      if (!Object.is(nextValue, numericValue)) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(nextValue));
        } catch {
          // State still converges even when persistence is unavailable.
        }
      }
      const nextValues = { ...valuesRef.current, [seedField.field_id]: nextValue };
      valuesRef.current = nextValues;
      setValues(nextValues);
    }
    // The fixed flag is tab-scoped (sessionStorage) — no cross-tab sync.
  }), [config.fields, currentOrganization, workflowId]);

  // Sort fields by display_order
  const sortedFields = [...config.fields].sort((a, b) => a.display_order - b.display_order);

  // Find controller field and get its current value
  const controllerField = getControllerField(sortedFields);
  const controllerValue = controllerField
    ? (values[controllerField.field_id] ? String(values[controllerField.field_id]) : undefined)
    : undefined;

  const captureSeedAdvance = (): (() => void) | null => {
    const capturedControllerValue = controllerField
      ? valuesRef.current[controllerField.field_id]
      : undefined;
    const snapshots = sortedFields.flatMap(field => {
      if (field.field_type !== 'seed' || fixedSeedsRef.current[field.field_id]) return [];
      const effective = controllerField
        ? getEffectiveConfig(field, controllerValue)
        : { config: field.config_data, hidden: false };
      if (effective.hidden) return [];
      const { min, max } = normalizeSeedBounds(
        effective.config?.min_value,
        effective.config?.max_value
      );
      return [{ fieldId: field.field_id, seed: valuesRef.current[field.field_id], min, max }];
    });
    if (snapshots.length === 0) return null;

    return () => {
      if (
        controllerField
        && !Object.is(valuesRef.current[controllerField.field_id], capturedControllerValue)
      ) return;
      snapshots.forEach(({ fieldId, seed, min, max }) => {
        if (fixedSeedsRef.current[fieldId] || !Object.is(valuesRef.current[fieldId], seed)) return;
        let nextSeed = nextRandomSeed(min, max);
        if (nextSeed === seed && min !== max) {
          nextSeed = offsetSeed(nextSeed, 1, min, max);
        }
        const persistedResult = currentOrganization
          ? persistSeedAdvance({
              storageKey: buildStorageKey(fieldId),
              fixedStorageKey: buildFixedStorageKey(fieldId),
              expectedSeed: seed,
              nextSeed,
              guards: controllerField
                  ? [{
                    storageKey: buildStorageKey(controllerField.field_id),
                    expectedValue: capturedControllerValue,
                    // Defaults are not persisted until the controller changes.
                    allowMissing: true,
                  }]
                : [],
            })
          : 'unavailable';
        if (persistedResult === 'unavailable') handleFieldChange(fieldId, nextSeed);
      });
    };
  };

  // Expose widget interface methods via ref
  useImperativeHandle(widgetRef, () => ({
    getPayloadData(): WidgetPayloadResult {
      const data: Record<string, any> = {};
      const seedFields: NonNullable<WidgetPayloadResult['seedFields']> = [];

      sortedFields.forEach(field => {
        // Skip fields hidden by controller variant
        if (field.field_type !== 'controller_dropdown' && controllerField) {
          const { hidden } = getEffectiveConfig(field, controllerValue);
          if (hidden) return;
        }

        // Hidden fields read values from config, not from user state
        if (field.field_type === 'hidden_text') {
          const { config: cfg } = getEffectiveConfig(field, controllerValue);
          data[field.field_id] = String(cfg.default_value ?? '');
          return;
        }
        if (field.field_type === 'hidden_number') {
          const { config: cfg } = getEffectiveConfig(field, controllerValue);
          data[field.field_id] = Number(cfg.default_value ?? 0);
          return;
        }
        if (field.field_type === 'hidden_prompt') {
          const { config: cfg } = getEffectiveConfig(field, controllerValue);
          data[field.field_id] = String(cfg.prompt_id ?? '');
          return;
        }

        let value = values[field.field_id];

        // If no value set yet, use default_value from effective config (respects variant overrides)
        if (value === undefined || value === null || value === '') {
          const effectiveConfig = (field.field_type !== 'controller_dropdown' && controllerField)
            ? getEffectiveConfig(field, controllerValue).config
            : field.config_data;
          value = effectiveConfig?.default_value;
        }

        if (field.field_type === 'multiselect') {
          const effectiveConfig = (controllerField)
            ? getEffectiveConfig(field, controllerValue).config
            : field.config_data;
          const normalized = normalizeEmbeddedMultiselectValue(
            value,
            effectiveConfig?.options,
            effectiveConfig?.maxItems
          );
          data[field.field_id] = JSON.stringify(normalized);
          return;
        }

        // Only include if we have a value
        if (value !== undefined && value !== null && value !== '') {
          // Special handling for image_size fields
          if (field.field_type === 'image_size' && typeof value === 'object' && 'width' in value && 'height' in value) {
            data[`${field.field_id}_width`] = value.width;
            data[`${field.field_id}_height`] = value.height;
          } else {
            data[field.field_id] = value;
            if (field.field_type === 'seed') {
              const effectiveConfig = controllerField
                ? getEffectiveConfig(field, controllerValue).config
                : field.config_data;
              const bounds = normalizeSeedBounds(
                effectiveConfig?.min_value,
                effectiveConfig?.max_value
              );
              seedFields.push({
                payloadKey: field.field_id,
                ...bounds,
              });
            }
          }
        }
      });

      return { data, ...(seedFields.length > 0 && { seedFields }) };
    },
    validateState(): WidgetValidationError[] {
      const errors: WidgetValidationError[] = [];
      sortedFields.forEach(field => {
        // Hidden field types have no user-interactive state — skip validation
        if (field.field_type === 'hidden_text' || field.field_type === 'hidden_number' || field.field_type === 'hidden_prompt') return;

        // Skip validation for fields hidden by controller variant
        if (field.field_type !== 'controller_dropdown' && controllerField) {
          const { hidden } = getEffectiveConfig(field, controllerValue);
          if (hidden) return;
        }

        const isRequired = field.config_data?.is_required;
        if (isRequired) {
          let value = values[field.field_id];
          // Fall back to variant-resolved default (consistent with getPayloadData and renderField)
          if (value === undefined || value === null || value === '') {
            const effectiveConfig = (field.field_type !== 'controller_dropdown' && controllerField)
              ? getEffectiveConfig(field, controllerValue).config
              : field.config_data;
            value = effectiveConfig?.default_value;
          }
          const effectiveConfig = (field.field_type !== 'controller_dropdown' && controllerField)
            ? getEffectiveConfig(field, controllerValue).config
            : field.config_data;
          const normalizedMultiselect = field.field_type === 'multiselect'
            ? normalizeEmbeddedMultiselectValue(value, effectiveConfig?.options, effectiveConfig?.maxItems)
            : null;
          if (value === undefined || value === null || value === '' ||
              (normalizedMultiselect ? normalizedMultiselect.length === 0 : Array.isArray(value) && value.length === 0)) {
            errors.push({
              field: field.field_id,
              message: `${field.label} is required`
            });
          }
        }
      });
      return errors;
    },
    isRequired(): boolean {
      return sortedFields.some(field => {
        if (!field.config_data?.is_required) return false;
        // Hidden field types are never user-interactive
        if (field.field_type === 'hidden_text' || field.field_type === 'hidden_number' || field.field_type === 'hidden_prompt') return false;
        if (field.field_type !== 'controller_dropdown' && controllerField) {
          const { hidden } = getEffectiveConfig(field, controllerValue);
          if (hidden) return false;
        }
        return true;
      });
    },
    advanceSeed(): void {
      captureSeedAdvance()?.();
    },
    captureSeedAdvance,
  }), [sortedFields, values, fixedSeeds, controllerValue]);

  // Initialize values from localStorage and defaults
  useEffect(() => {
    const initialValues: Record<string, any> = {};
    const initialFixedSeeds: Record<string, boolean> = {};
    let restoredControllerValue = controllerValue;

    // Set default values from field configs
    sortedFields.forEach(field => {
      if (field.config_data?.default_value !== undefined) {
        initialValues[field.field_id] = field.config_data.default_value;
      }
    });

    // Load from localStorage
    if (typeof window !== 'undefined' && currentOrganization) {
      sortedFields.forEach(field => {
        try {
          const storageKey = `embedded_widget_${workflowId}_${field.field_id}_${currentOrganization.id}`;
          const storedValue = localStorage.getItem(storageKey);
          if (storedValue !== null) {
            initialValues[field.field_id] = JSON.parse(storedValue);
          }
          if (field.field_type === 'seed') {
            // sessionStorage on purpose: the lock lives only within the
            // current tab and never survives a browser restart.
            const storedFixed = sessionStorage.getItem(buildFixedStorageKey(field.field_id));
            if (storedFixed !== null) {
              initialFixedSeeds[field.field_id] = JSON.parse(storedFixed) === true;
            }
          }
        } catch (e) {
          // Failed to load from localStorage
        }
      });
    }

    // Clamp slider/int_field values and reset invalid dropdown/multiselect for restored controller value
    if (controllerField) {
      const ctrlVal = initialValues[controllerField.field_id];
      let ctrlStr = ctrlVal !== undefined && ctrlVal !== null && ctrlVal !== '' ? String(ctrlVal) : undefined;

      // Validate controller value against current options — stale values from
      // renamed/deleted options must not survive in localStorage
      if (ctrlStr) {
        const ctrlConfig = controllerField.config_data || {};
        const ctrlOptions = parseDropdownOptions(ctrlConfig.options);
        if (!ctrlOptions.some(o => o.value === ctrlStr)) {
          const validDefault = ctrlConfig.default_value && ctrlOptions.some(o => o.value === String(ctrlConfig.default_value));
          const fallback = validDefault ? ctrlConfig.default_value : ctrlOptions[0]?.value || '';
          initialValues[controllerField.field_id] = fallback;
          ctrlStr = fallback || undefined;
        }
      }
      restoredControllerValue = ctrlStr;

      // Sanitize dependent field values against effective config (variant-resolved
      // when ctrlStr is set, base config when ctrlStr is empty/undefined).
      // This ensures stale localStorage values are cleaned up regardless of
      // whether a controller value is active.
      sortedFields.forEach(f => {
        if (f.field_type === 'controller_dropdown') return;
        const { config: effectiveConfig, hidden } = getEffectiveConfig(f, ctrlStr);
        if (hidden) return;

        if (f.field_type === 'seed') {
          const currentValue = initialValues[f.field_id];
          if (typeof currentValue === 'number') {
            const { min, max } = normalizeSeedBounds(
              effectiveConfig.min_value,
              effectiveConfig.max_value
            );
            initialValues[f.field_id] = clampSeed(currentValue, min, max);
          }
        } else if (f.field_type === 'slider' || f.field_type === 'int_field') {
          const currentValue = initialValues[f.field_id];
          if (typeof currentValue === 'number') {
            if (effectiveConfig.min_value !== undefined && currentValue < effectiveConfig.min_value) {
              initialValues[f.field_id] = effectiveConfig.min_value;
            } else if (effectiveConfig.max_value !== undefined && currentValue > effectiveConfig.max_value) {
              initialValues[f.field_id] = effectiveConfig.max_value;
            }
          }
        }

        // Reset dropdown values not in effective options
        if (f.field_type === 'dropdown' && effectiveConfig.options) {
          const currentVal = initialValues[f.field_id];
          if (currentVal) {
            const options = parseDropdownOptions(effectiveConfig.options);
            if (!options.some(o => o.value === String(currentVal))) {
              const validDefault = effectiveConfig.default_value && options.some(o => o.value === String(effectiveConfig.default_value));
              initialValues[f.field_id] = validDefault ? effectiveConfig.default_value : options[0]?.value || '';
            }
          }
        }

        // Reset dropdown_prompt values not in effective options
        if (f.field_type === 'dropdown_prompt' && effectiveConfig.options) {
          const currentVal = initialValues[f.field_id];
          if (currentVal) {
            const promptOptions = Array.isArray(effectiveConfig.options)
              ? effectiveConfig.options.filter((opt: any) => opt.prompt_id)
              : [];
            if (!promptOptions.some((o: any) => String(o.prompt_id) === String(currentVal))) {
              const validDefault = effectiveConfig.default_value && promptOptions.some((o: any) => String(o.prompt_id) === String(effectiveConfig.default_value));
              initialValues[f.field_id] = validDefault ? effectiveConfig.default_value : '';
            }
          }
        }

      });
    }

    // Normalize every multiselect, including workflows without a controller.
    // This migrates legacy text values, drops removed options, and clamps stale
    // localStorage/default values before they can be rendered or submitted.
    sortedFields.forEach(field => {
      if (field.field_type !== 'multiselect') return;
      const { config: effectiveConfig, hidden } = controllerField
        ? getEffectiveConfig(field, restoredControllerValue)
        : { config: field.config_data, hidden: false };
      if (hidden) return;
      initialValues[field.field_id] = normalizeEmbeddedMultiselectValue(
        initialValues[field.field_id],
        effectiveConfig?.options,
        effectiveConfig?.maxItems
      );
    });

    sortedFields.forEach(field => {
      const currentValue = initialValues[field.field_id];
      if (field.field_type !== 'seed') return;
      const effectiveConfig = controllerField
        ? getEffectiveConfig(field, restoredControllerValue).config
        : field.config_data;
      const { min, max } = normalizeSeedBounds(
        effectiveConfig?.min_value,
        effectiveConfig?.max_value
      );
      initialValues[field.field_id] = currentValue !== undefined && currentValue !== null && currentValue !== ''
        ? clampSeed(Number(currentValue), min, max)
        : nextRandomSeed(min, max);
      if (typeof window !== 'undefined' && currentOrganization) {
        try {
          localStorage.setItem(
            buildStorageKey(field.field_id),
            JSON.stringify(initialValues[field.field_id])
          );
        } catch (e) {
          // Failed to save to localStorage
        }
      }
    });

    valuesRef.current = initialValues;
    fixedSeedsRef.current = initialFixedSeeds;
    setValues(initialValues);
    setFixedSeeds(initialFixedSeeds);
  }, [workflowId, currentOrganization, sortedFields.length]);

  const handleFieldChange = (fieldId: string, value: any) => {
    const newValues: Record<string, any> = { [fieldId]: value };

    // When controller value changes, clamp dependent numeric values to the new range
    // and reset dropdown/multiselect values that are no longer valid
    if (controllerField && controllerField.field_id === fieldId) {
      const newControllerValue = String(value);
      sortedFields.forEach(f => {
        if (f.field_type === 'controller_dropdown') return;
        const { config: effectiveConfig, hidden } = getEffectiveConfig(f, newControllerValue);
        if (f.field_type === 'seed' && !hidden) {
          const currentValue = valuesRef.current[f.field_id];
          if (typeof currentValue === 'number') {
            const { min, max } = normalizeSeedBounds(
              effectiveConfig.min_value,
              effectiveConfig.max_value
            );
            newValues[f.field_id] = clampSeed(currentValue, min, max);
          }
        } else if ((f.field_type === 'slider' || f.field_type === 'int_field') && !hidden) {
          const currentValue = valuesRef.current[f.field_id];
          if (typeof currentValue === 'number') {
            if (effectiveConfig.min_value !== undefined && currentValue < effectiveConfig.min_value) {
              newValues[f.field_id] = effectiveConfig.min_value;
            } else if (effectiveConfig.max_value !== undefined && currentValue > effectiveConfig.max_value) {
              newValues[f.field_id] = effectiveConfig.max_value;
            }
          }
        }

        // Reset dropdown values that are no longer valid in new effective options
        if (f.field_type === 'dropdown' && !hidden && effectiveConfig.options) {
          const currentVal = valuesRef.current[f.field_id];
          if (currentVal) {
            const options = parseDropdownOptions(effectiveConfig.options);
            if (!options.some(o => o.value === String(currentVal))) {
              const validDefault = effectiveConfig.default_value && options.some(o => o.value === String(effectiveConfig.default_value));
              newValues[f.field_id] = validDefault ? effectiveConfig.default_value : options[0]?.value || '';
            }
          }
        }

        // Reset dropdown_prompt values that are no longer valid
        if (f.field_type === 'dropdown_prompt' && !hidden && effectiveConfig.options) {
          const currentVal = valuesRef.current[f.field_id];
          if (currentVal) {
            const promptOptions = Array.isArray(effectiveConfig.options)
              ? effectiveConfig.options.filter((opt: any) => opt.prompt_id)
              : [];
            if (!promptOptions.some((o: any) => String(o.prompt_id) === String(currentVal))) {
              const validDefault = effectiveConfig.default_value && promptOptions.some((o: any) => String(o.prompt_id) === String(effectiveConfig.default_value));
              newValues[f.field_id] = validDefault ? effectiveConfig.default_value : '';
            }
          }
        }

        // Filter multiselect values that are no longer valid
        if (f.field_type === 'multiselect' && !hidden && effectiveConfig.options) {
          newValues[f.field_id] = normalizeEmbeddedMultiselectValue(
            valuesRef.current[f.field_id],
            effectiveConfig.options,
            effectiveConfig.maxItems
          );
        }
      });
    }

    const nextValues = { ...valuesRef.current, ...newValues };
    valuesRef.current = nextValues;
    if (mountedRef.current) setValues(nextValues);

    // Save to localStorage
    if (typeof window !== 'undefined' && currentOrganization) {
      try {
        for (const [key, val] of Object.entries(newValues)) {
          const storageKey = `embedded_widget_${workflowId}_${key}_${currentOrganization.id}`;
          localStorage.setItem(storageKey, JSON.stringify(val));
        }
      } catch (error) {
        // Failed to save to localStorage
      }
    }
  };

  const handleFixedChange = (fieldId: string, checked: boolean) => {
    const nextFixedSeeds = { ...fixedSeedsRef.current, [fieldId]: checked };
    fixedSeedsRef.current = nextFixedSeeds;
    if (mountedRef.current) setFixedSeeds(nextFixedSeeds);

    if (typeof window !== 'undefined' && currentOrganization) {
      try {
        sessionStorage.setItem(buildFixedStorageKey(fieldId), JSON.stringify(checked));
      } catch (error) {
        // Failed to save to sessionStorage
      }
    }
  };

  const renderField = (field: EmbeddedField) => {
    // Apply variant resolution for non-controller fields
    let config = field.config_data || {};
    if (field.field_type !== 'controller_dropdown' && controllerField) {
      const { config: effectiveConfig, hidden } = getEffectiveConfig(field, controllerValue);
      if (hidden) return null;
      config = effectiveConfig;
    }

    const value = values[field.field_id] !== undefined
      ? values[field.field_id]
      : (config.default_value !== undefined ? config.default_value : '');

    const fieldProps = {
      id: field.field_id,
      value,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleFieldChange(field.field_id, e.target.value),
      className: "w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 hover:border-gray-500 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500/20 transition-all duration-200 text-sm"
    };

    switch (field.field_type) {
      case 'string_field':
        const stringValue = value || '';
        const maxLength = config.max_length;
        const minLength = config.min_length || 0;
        const isMultiline = config.multiline ?? false;
        const textareaRows = config.rows ?? 3;
        const isStringOutOfRange = (maxLength && stringValue.length > maxLength) ||
                                   (minLength > 0 && stringValue.length < minLength && stringValue.length > 0);
        const stringErrorClass = isStringOutOfRange ? 'border-red-500 focus:border-red-400 focus:ring-red-500/20' : '';

        return (
          <div>
            {isMultiline ? (
              <textarea
                id={field.field_id}
                value={stringValue}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleFieldChange(field.field_id, e.target.value)}
                rows={textareaRows}
                maxLength={maxLength}
                placeholder={config.placeholder || ''}
                className={`${fieldProps.className} resize-y min-h-[80px] ${stringErrorClass}`}
              />
            ) : (
              <input
                type="text"
                {...fieldProps}
                placeholder={config.placeholder || ''}
                maxLength={maxLength}
                className={`${fieldProps.className} ${stringErrorClass}`}
              />
            )}
            {isStringOutOfRange && (
              <p className="text-xs text-red-400 mt-1">
                {minLength && stringValue.length < minLength
                  ? `Minimum length is ${minLength} characters`
                  : `Maximum length is ${maxLength} characters`
                }
              </p>
            )}
            {maxLength && !isStringOutOfRange && (
              <p className="text-xs text-gray-400 text-right mt-1">
                {stringValue.length}/{maxLength}
              </p>
            )}
          </div>
        );

      case 'int_field':
        const intValue = value !== '' && value !== null && value !== undefined ? parseInt(value) : null;
        const minValue = config.min_value ?? -Infinity;
        const maxValue = config.max_value ?? Infinity;
        const isIntOutOfRange = intValue !== null && (intValue < minValue || intValue > maxValue);

        return (
          <div>
            <input
              type="number"
              {...fieldProps}
              onChange={(e) => {
                const newValue = e.target.value === '' ? '' : parseInt(e.target.value) || 0;
                handleFieldChange(field.field_id, newValue);
              }}
              placeholder={config.placeholder || ''}
              min={minValue !== -Infinity ? minValue : undefined}
              max={maxValue !== Infinity ? maxValue : undefined}
              className={`${fieldProps.className} ${isIntOutOfRange ? 'border-red-500 focus:border-red-400 focus:ring-red-500/20' : ''}`}
            />
            {isIntOutOfRange && (
              <p className="text-xs text-red-400 mt-1">
                {intValue < minValue
                  ? `Minimum value is ${minValue}`
                  : `Maximum value is ${maxValue}`
                }
              </p>
            )}
          </div>
        );

      case 'slider':
        const sliderMin = config.min_value || 0;
        const sliderMax = config.max_value || 100;
        const sliderStep = config.step || 1;
        const sliderDesign = config.design || 'basic';

        // Nullish (not ||) so a legit 0 is kept instead of snapping back to min.
        const displayValue = value ?? (config.default_value ?? sliderMin);
        const roundedDisplayValue = Math.round(displayValue * 1000) / 1000;

        const validValues: number[] = [];
        validValues.push(sliderMin);

        let currentVal = sliderMin + sliderStep;
        while (currentVal < sliderMax) {
          validValues.push(Math.round(currentVal * 1000) / 1000);
          currentVal += sliderStep;
        }

        if (validValues[validValues.length - 1] !== sliderMax) {
          validValues.push(sliderMax);
        }

        const findClosestValue = (target: number) => {
          return validValues.reduce((prev, curr) =>
            Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
          );
        };

        const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = parseFloat(e.target.value);
          const closestValue = findClosestValue(rawValue);
          const roundedValue = Math.round(closestValue * 1000) / 1000;
          handleFieldChange(field.field_id, roundedValue);
        };

        const htmlStep = Math.min(sliderStep, (sliderMax - sliderMin) / 100);

        const sliderContent = (
          <>
            <input
              type="range"
              {...fieldProps}
              onChange={handleSliderChange}
              min={sliderMin}
              max={sliderMax}
              step={htmlStep}
              value={displayValue}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>{sliderMin}</span>
              <span className="font-medium text-white">
                {roundedDisplayValue}
              </span>
              <span>{sliderMax}</span>
            </div>
          </>
        );

        if (sliderDesign === 'special1') {
          return sliderContent;
        }

        return (
          <div className="space-y-2">
            {sliderContent}
          </div>
        );

      case 'dropdown':
        const dropdownOptions = parseDropdownOptions(config.options);

        const dropdownValue = value || config.default_value || '';

        return (
          <CustomSelect
            value={dropdownValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            options={dropdownOptions}
            placeholder={config.placeholder || "Select option..."}
            className="w-full"
            language={selectedLanguage}
          />
        );

      case 'dropdown_prompt': {
        const dpOptions = Array.isArray(config.options)
          ? config.options
              .filter((opt: any) => opt.label && opt.prompt_id)
              .map((opt: any) => ({ label: opt.label, value: String(opt.prompt_id) }))
          : [];
        const dpValue = value || config.default_value || '';
        return (
          <CustomSelect
            value={dpValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            options={dpOptions}
            placeholder={config.placeholder || "Select option..."}
            className="w-full"
            language={selectedLanguage}
          />
        );
      }

      case 'multiselect':
        const multiselectOptions = parseDropdownOptions(config.options);
        const maxItems = parseMaxItems(config.maxItems);
        const selectedValues = normalizeEmbeddedMultiselectValue(value, config.options, maxItems);
        return (
          <div className="space-y-1">
            {maxItems !== undefined && (
              <p className="text-xs text-gray-500">
                {(t.widgets?.maxSelections || 'Maximum: {max}').replace('{max}', String(maxItems))}
              </p>
            )}
            <MultiSelect
              id={field.field_id}
              options={multiselectOptions}
              value={selectedValues}
              onChange={(newValues) => {
                if (maxItems !== undefined && newValues.length > maxItems) return;
                handleFieldChange(field.field_id, newValues);
              }}
              placeholder={config.placeholder || 'Select options...'}
              className="w-full"
              language={selectedLanguage}
            />
          </div>
        );

      case 'image_size':
        const imageSizeValue = value || { width: config.default_width || 768, height: config.default_height || 1024 };

        let aspectRatios = config.aspect_ratios;
        if (aspectRatios && Array.isArray(aspectRatios) && aspectRatios.length > 0) {
          if (typeof aspectRatios[0] === 'string') {
            aspectRatios = aspectRatios.map((ratio: string) => ({
              ratio,
              width: null,
              height: null
            }));
          }
        }

        return (
          <ImageSizeInput
            value={imageSizeValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            config={{
              min_width: config.min_width || 64,
              max_width: config.max_width || 2048,
              min_height: config.min_height || 64,
              max_height: config.max_height || 2048,
              default_width: config.default_width || 768,
              default_height: config.default_height || 1024,
              step: config.step || 8,
              aspect_ratios: aspectRatios,
              default_aspect_ratio: config.default_aspect_ratio
            }}
            className="w-full"
          />
        );

      case 'seed':
        const seedValue = value ?? config.default_value ?? null;
        const seedBounds = normalizeSeedBounds(config.min_value, config.max_value);
        return (
          <SeedInput
            value={seedValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            checked={fixedSeeds[field.field_id] ?? false}
            onFixedChange={(checked) => handleFixedChange(field.field_id, checked)}
            fixedLabel={t.fixedSeedLabel}
            fixedTooltip={t.fixedSeedTooltip}
            config={{
              min_value: seedBounds.min,
              max_value: seedBounds.max
            }}
            className="w-full"
          />
        );

      case 'controller_dropdown': {
        const ctrlOptions = parseDropdownOptions(config.options);
        const ctrlValue = value || config.default_value || '';
        return (
          <CustomSelect
            value={ctrlValue}
            onChange={(newValue) => handleFieldChange(field.field_id, newValue)}
            options={ctrlOptions}
            placeholder={config.placeholder || "Select option..."}
            className="w-full"
            language={selectedLanguage}
          />
        );
      }

      case 'hidden_text':
      case 'hidden_number':
      case 'hidden_prompt':
        return null;

      default:
        return (
          <div className="text-xs text-gray-500 italic">
            Unsupported field type: {field.field_type}
          </div>
        );
    }
  };

  if (sortedFields.length === 0) {
    return null;
  }

  // Filter out hidden fields for rendering
  const visibleFields = sortedFields.filter(field => {
    // Hidden field types are never rendered
    if (field.field_type === 'hidden_text' || field.field_type === 'hidden_number' || field.field_type === 'hidden_prompt') return false;
    if (field.field_type === 'controller_dropdown' || !controllerField) return true;
    const { hidden } = getEffectiveConfig(field, controllerValue);
    return !hidden;
  });

  const content = (
    <div className="space-y-4">
      {visibleFields.map((field, index) => {
        const fieldConfig = field.config_data || {};
        const isHighlighted = fieldConfig.highlight === true;
        const isSpecialSlider = field.field_type === 'slider' && fieldConfig.design === 'special1';

        return (
          <React.Fragment key={field.field_id}>
            {index > 0 && <div className="border-t border-gray-700/50"></div>}

            <div className={isHighlighted || isSpecialSlider ? "bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-500/40 rounded-lg p-4" : ""}>
              {/* Label and Tooltip */}
              <div className="flex items-center justify-between mb-2">
                <label className={`${isHighlighted || isSpecialSlider ? 'text-sm font-semibold text-blue-200' : 'text-xs font-medium text-gray-300'}`}>
                  {field.label}
                </label>
                {field.tooltip && (
                  <Tooltip content={field.tooltip}>
                    <div className={`w-4 h-4 ${isHighlighted || isSpecialSlider ? 'bg-blue-700/50 border-blue-500' : 'bg-gray-700/50 border-gray-600'} border rounded-full flex items-center justify-center cursor-help ${isHighlighted || isSpecialSlider ? 'hover:bg-blue-600/50' : 'hover:bg-gray-600/50'} transition-colors duration-200`}>
                      <span className={`text-xs font-medium ${isHighlighted || isSpecialSlider ? 'text-blue-200' : 'text-gray-300'}`}>?</span>
                    </div>
                  </Tooltip>
                )}
              </div>

              {/* Field Input */}
              {field.field_type === 'slider' && fieldConfig.design === 'special1' ? (
                <div className="space-y-2">
                  {renderField(field)}
                </div>
              ) : (
                renderField(field)
              )}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <BaseWidget
      title={config.name}
      icon={<Icon name={ICONS.SETTINGS} size={16} className="text-current" /> as any}
      description={config.description || undefined}
      language={selectedLanguage}
      className={className}
      widgetId={`embedded_${workflowId}`}
    >
      {content}
    </BaseWidget>
  );
}
